import { supabase } from '../lib/supabase';
import { assertContentIsAllowed } from '../lib/contentModeration';

export const profileService = {
  async uploadBannerImage(file: File, userId: string) {
    const fileExt = file.name.split('.').pop();
    const fileName = `banner-${Date.now()}.${fileExt}`;
    const filePath = `${userId}/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('profile-images')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    const { data } = supabase.storage
      .from('profile-images')
      .getPublicUrl(filePath);

    return data.publicUrl;
  },

  async uploadAvatarImage(file: File, userId: string) {
    const fileExt = file.name.split('.').pop();
    const fileName = `avatar-${Date.now()}.${fileExt}`;
    const filePath = `${userId}/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('profile-images')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    const { data } = supabase.storage
      .from('profile-images')
      .getPublicUrl(filePath);

    return data.publicUrl;
  },

  async updateProfile(userId: string, updates: {
    full_name?: string;
    bio?: string;
    banner_url?: string;
    avatar_url?: string;
    location?: string;
    website?: string;
    art_categories?: string[];
    expo_push_token?: string;
  }) {
    await assertContentIsAllowed([
      updates.full_name,
      updates.bio,
      updates.location,
      Array.isArray(updates.art_categories) ? updates.art_categories.join(' ') : undefined,
    ]);

    const { data, error } = await supabase
      .from('profiles')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async getProfile(userId: string) {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) throw error;
    return data;
  },

  async getProfileByUsername(username: string) {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('username', username)
      .single();

    if (error) throw error;
    return data;
  },

  async getUserPosts(userId: string, options?: {
    includeAll?: boolean;
    moderatorView?: boolean;
    viewerId?: string | null;
  }) {
    let query = supabase
      .from('posts')
      .select(`
        *,
        profiles!posts_author_id_fkey (
          username,
          full_name,
          avatar_url
        ),
        media_files (
          id,
          type,
          url,
          file_name,
          width,
          height
        )
      `)
      .eq('author_id', userId)
      .order('created_at', { ascending: false });

    if (!options?.includeAll) {
      query = query.eq('moderation_status', 'approved');
    }

    const { data, error } = await query;
    if (error) throw error;

    return (data || []).map((post) => {
      const comments = Array.isArray((post as { comments?: Array<{ moderation_status?: string; author_id: string }> }).comments)
        ? (post as { comments: Array<{ moderation_status?: string; author_id: string }> }).comments
        : [];

      const filteredComments = comments.filter((comment: { moderation_status?: string; author_id: string }) => {
        if (!comment?.moderation_status || comment.moderation_status === 'approved') return true;
        if (options?.moderatorView) return true;
        if (options?.viewerId && comment.author_id === options.viewerId) return true;
        return false;
      });

      return {
        ...post,
        comments: filteredComments,
      };
    });
  },

  async getUserStats(userId: string) {
    // Get posts count
    const { count: postsCount, error: postsError } = await supabase
      .from('posts')
      .select('*', { count: 'exact', head: true })
      .eq('author_id', userId);

    if (postsError) throw postsError;

    // Get user's post IDs first
    const { data: userPosts, error: userPostsError } = await supabase
      .from('posts')
      .select('id')
      .eq('author_id', userId);

    if (userPostsError) throw userPostsError;

    const postIds = userPosts?.map(post => post.id) || [];

    // Get total likes for user's posts
    const { count: likesCount, error: likesError } = await supabase
      .from('likes')
      .select('id', { count: 'exact' })
      .in('post_id', postIds);

    if (likesError) throw likesError;


    return {
      posts: postsCount || 0,
      likes: likesCount || 0,
    };
  },

  async searchUsers(query: string) {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .or(`username.ilike.%${query}%,full_name.ilike.%${query}%`)
      .limit(10);

    if (error) throw error;
    return data;
  },
};
